// src/components/volwassenheid/VolwassenheidBoard.jsx
import React, { useEffect, useState } from "react";
import { loadStateVW, refreshStateVW, saveStateVW, defaultStateVW } from "../../state/stateRepo";
import { useStatePolling } from "../../state/useStatePolling";
import SideNav from "../SideNav";
import VolwassenheidTab from "./VolwassenheidTab";
import { volwassenheidCatalog } from "../../content/volwassenheid.catalog";

const TOTAALOVERZICHT_DOMAIN = "__totaaloverzicht__";

const PAGE_BG =
  "radial-gradient(circle at 20% 10%, #92bad4, rgba(255,255,255,0.00) 45%), " +
  "radial-gradient(circle at 90% 80%, #92bad4, rgba(244,124,0,0.00) 50%), #ffffff";

const PIJLER_TABS = [
  { id: "overview", label: "Overzicht" },
  ...volwassenheidCatalog.pijlers.map((p) => ({
    id: p.id,
    label: p.title.split("—").slice(-1)[0].trim(),
  })),
];

export default function VolwassenheidBoard({ projectId }) {
  const [state, setState] = useState(null);
  const [project, setProject] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  // Fetch project info
  useEffect(() => {
    fetch("/api/projects")
      .then((r) => r.json())
      .then((data) => {
        const items = Array.isArray(data) ? data : data.projects || [];
        const found = items.find((p) => p.ProjectId === projectId);
        if (found) setProject(found);
      })
      .catch((e) => console.error("Failed to load project info:", e));
  }, [projectId]);

  // Fetch logged-in user email
  useEffect(() => {
    fetch("/.auth/me")
      .then((r) => r.json())
      .then((data) => {
        const principal = Array.isArray(data) ? data[0] : data?.clientPrincipal;
        const email =
          principal?.userDetails ||
          principal?.claims?.find((c) => c.typ === "preferred_username" || c.typ === "email")?.val ||
          null;
        if (email) setUserEmail(email);
      })
      .catch((e) => console.error("Failed to load auth info:", e));
  }, []);

  useEffect(() => {
    document.title = "GiGA – Datavolwassenheid Dashboard";
  }, []);

  // Load state on mount
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const s = await loadStateVW(projectId);
        if (!cancelled) setState(s);
      } catch (e) {
        console.error("Failed to load state:", e);
        if (!cancelled) setState(defaultStateVW());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Fallback: als project later laadt dan state en activeDomain nog null is, stel het in
  useEffect(() => {
    if (!project?.Domains || !state) return;
    if (state.ui?.activeDomain) return;

    const firstDomain = project.Domains
      .split(";")
      .map((d) => d.trim())
      .find(Boolean) ?? null;

    if (!firstDomain) return;

    setState((prev) => ({
      ...prev,
      ui: { ...(prev.ui || {}), activeDomain: firstDomain },
    }));
  }, [project?.Domains, state]);

  useStatePolling(projectId, refreshStateVW, setState);

  async function handleSave() {
    if (!state) return;
    setSaving(true);
    try {
      const r = await refreshStateVW(projectId);
      if (r.updated && r.state) {
        window.alert("Er is een recentere versie beschikbaar. Deze wordt nu ingeladen.");
        setState(r.state);
        return;
      }
      await saveStateVW(projectId, state, {
        onConflict: (freshState) => setState(freshState),
        onError: (e) => console.error("Save failed:", e),
      });
    } finally {
      setSaving(false);
    }
  }

  async function doRefresh() {
    try {
      setRefreshing(true);
      const r = await refreshStateVW(projectId);
      if (r.updated && r.state) {
        setState(r.state);
      }
    } catch (e) {
      console.error("Refresh failed:", e);
    } finally {
      setRefreshing(false);
    }
  }

  if (!state) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: PAGE_BG }}>
      <div className="portal-loading">
        <div className="portal-spinner" />
        <p className="portal-loading-text">Project wordt geladen…</p>
      </div>
    </div>
  );

  // Derived state
  const domains = (project?.Domains || "")
    .split(";")
    .map((d) => d.trim())
    .filter(Boolean);

  const activeDomain = state.ui?.activeDomain ?? domains[0] ?? "";
  const isTotaaloverzicht = activeDomain === TOTAALOVERZICHT_DOMAIN;
  const activePijler = state.ui?.activePijler ?? "overview";
  const activeNiveau = state.ui?.activeNiveau ?? "niveau-1";
  const checkedActions = state.data?.checkedActions || {};
  const updates = state.data?.updates || {};

  const setActiveDomain = (domain) =>
    setState((prev) => ({
      ...prev,
      ui: { ...(prev.ui || {}), activeDomain: domain },
    }));

  const setActivePijler = (pijler) =>
    setState((prev) => ({
      ...prev,
      ui: { ...(prev.ui || {}), activePijler: pijler },
    }));

  const setActiveNiveau = (niveau) =>
    setState((prev) => ({
      ...prev,
      ui: { ...(prev.ui || {}), activeNiveau: niveau },
    }));

  async function autoSave(nextState) {
    try {
      await saveStateVW(projectId, nextState, {
        onConflict: (freshState) => setState(freshState),
        onError: (e) => console.error("Auto-save failed:", e),
      });
    } catch (e) {
      console.error("Auto-save failed:", e);
    }
  }

  const onToggle = (actionKey, checked) => {
    const nextState = {
      ...state,
      data: {
        ...(state.data || {}),
        checkedActions: {
          ...(state.data?.checkedActions || {}),
          [actionKey]: checked,
        },
      },
    };
    setState(nextState);
    autoSave(nextState);
  };

  const onSaveUpdate = (updateKey, updateObject) => {
    const existing = state.data?.updates?.[updateKey] || [];
    const nextState = {
      ...state,
      data: {
        ...(state.data || {}),
        updates: {
          ...(state.data?.updates || {}),
          [updateKey]: [...existing, updateObject],
        },
      },
    };
    setState(nextState);
    autoSave(nextState);
  };

  const onDeleteUpdate = (updateKey, timestamp) => {
    const existing = state.data?.updates?.[updateKey] || [];
    const nextState = {
      ...state,
      data: {
        ...(state.data || {}),
        updates: {
          ...(state.data?.updates || {}),
          [updateKey]: existing.filter((u) => u.timestamp !== timestamp),
        },
      },
    };
    setState(nextState);
    autoSave(nextState);
  };

  return (
    <div className="min-h-screen" style={{ background: PAGE_BG }}>
      <SideNav />

      {/* Header */}
      <div className="header-3d">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="dashboard-header-main">
            <div className="dashboard-header-title-group">
              <div className="logo-tile logo-tile--header">
                {project?.LogoPath && (
                  <img
                    className="logo-img"
                    src={process.env.PUBLIC_URL + project.LogoPath}
                    alt={project?.ProjectName ?? projectId}
                  />
                )}
              </div>

              <div>
                <h1 className="text-2xl font-semibold text-white">
                  {project?.ProjectName ?? projectId}
                </h1>
                <p className="mt-1 text-sm text-slate-200">
                  Stuur op datavolwassenheid per domein en pijler.
                </p>
              </div>
            </div>

            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={doRefresh}
                disabled={refreshing}
                className="login-btn"
                style={{ width: "auto", minWidth: "140px", marginTop: 0, marginBottom: 0 }}
              >
                {refreshing ? "Vernieuwen..." : "Vernieuwen"}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="login-btn"
                style={{ width: "auto", minWidth: "140px", marginTop: 0, marginBottom: 0 }}
              >
                {saving ? "Opslaan..." : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Domain tabs */}
      {domains.length > 0 && (
        <div>
          <div
            className="max-w-6xl mx-auto flex flex-wrap"
            style={{ padding: "10px 14px", gap: "8px" }}
          >
            <button
              key="__totaaloverzicht__"
              type="button"
              className={"tab-3d " + (isTotaaloverzicht ? "active" : "")}
              style={{ fontWeight: 800, letterSpacing: "0.01em" }}
              onClick={() => setActiveDomain(TOTAALOVERZICHT_DOMAIN)}
            >
              Totaaloverzicht
            </button>
            <span style={{ width: "1px", alignSelf: "stretch", background: "#c4d5e3", margin: "2px 4px" }} />
            {domains.map((domain) => (
              <button
                key={domain}
                type="button"
                className={"tab-3d text-sm " + (activeDomain === domain ? "active" : "")}
                onClick={() => setActiveDomain(domain)}
              >
                {domain}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main card */}
      <div className="max-w-6xl mx-auto px-4 py-5">
        <div className="card-3d rounded-2xl overflow-hidden" style={{ background: "#31719D" }}>

          {/* Pijler tabs */}
          {!isTotaaloverzicht && (
            <div
              className="flex flex-wrap"
              style={{ background: "#31719D", padding: "10px 14px", gap: "8px" }}
            >
              {PIJLER_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={"tab-3d text-sm " + (activePijler === t.id ? "active" : "")}
                  onClick={() => setActivePijler(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="px-4 py-5" style={{ background: "#31719D" }}>
            <VolwassenheidTab
              pijlerId={isTotaaloverzicht ? "overview" : activePijler}
              activeNiveau={activeNiveau}
              onSetNiveau={setActiveNiveau}
              domain={activeDomain}
              domains={domains}
              checkedActions={checkedActions}
              updates={updates}
              userEmail={userEmail}
              onToggle={onToggle}
              onSaveUpdate={onSaveUpdate}
              onDeleteUpdate={onDeleteUpdate}
            />
          </div>
        </div>

        <p className="text-[11px] text-center mt-4" style={{ color: "#000000" }}>
          © Datavolwassenheid.nl® B.V. Alle rechten voorbehouden. Dit dashboard en alle daarin opgenomen gegevens, visualisaties, teksten, modellen en methodieken zijn beschermd op grond van de Auteurswet en overige toepasselijke wetgeving. Verveelvoudiging, openbaarmaking, verspreiding of gebruik, geheel of gedeeltelijk, zonder voorafgaande schriftelijke toestemming van Datavolwassenheid.nl B.V. is niet toegestaan.
        </p>
      </div>
    </div>
  );
}
